// useWorkflowTaskChecklistProgress.js

import { useEffect, useMemo, useState } from 'react';

import { subscribeToChecklistForField } from 'services/checklists/ChecklistService';
import { getNodeType } from 'services/nodetypes/NodeTypesCacheService';
import {
  computeWorkflowTaskChecklistProgress,
  formatWorkflowTaskChecklistPercent,
  readWorkflowTaskChecklistRecipe,
  resolveTaskChecklistFieldName,
} from 'node/missions/mission/utils/workflowStepChecklistUtils';
import { WORKFLOW_STEP_TASK_TYPE_KEY } from 'node/missions/mission/utils/mintWorkflowStepTaskNodes';

const EMPTY_PROGRESS = Object.freeze({
  totalItems: 0,
  completedItems: 0,
  isComplete: false,
});

/**
 * Live Task Checklist progress for a workflow people step.
 *
 * Subscribes even when the checklist UI is collapsed so the title can
 * show percent complete.
 *
 * @param {object} params
 * @param {object|null} [params.task]
 * @param {object|null} [params.stepNode]
 * @param {object|null} [params.template]
 * @returns {{
 *   progress: { totalItems: number, completedItems: number, isComplete: boolean },
 *   percentLabel: string,
 * }}
 */
export default function useWorkflowTaskChecklistProgress({
  task = null,
  stepNode = null,
  template = null,
} = {}) {
  const templateTask = useMemo(() => {
    const taskKey = String(task?.key || '').trim();
    if (!taskKey || !template) return null;
    return (Array.isArray(template?.tasks) ? template.tasks : [])
      .find((entry) => String(entry?.key || '').trim() === taskKey) || null;
  }, [task?.key, template]);

  const recipe = useMemo(
    () => readWorkflowTaskChecklistRecipe(task, templateTask),
    [task, templateTask],
  );

  const [snapshotItems, setSnapshotItems] = useState([]);
  const [hasSnapshot, setHasSnapshot] = useState(false);

  const baseKey = String(stepNode?.baseKey || '').trim();
  const nodeKey = String(stepNode?.key || '').trim();
  const fieldName = useMemo(
    () => resolveTaskChecklistFieldName(
      getNodeType(WORKFLOW_STEP_TASK_TYPE_KEY) || null
    ),
    [],
  );

  useEffect(() => {
    setSnapshotItems([]);
    setHasSnapshot(false);

    if (!baseKey || !nodeKey || !fieldName || recipe.items.length === 0) {
      return undefined;
    }

    return subscribeToChecklistForField({
      baseKey,
      nodeKey,
      fieldName,
    }, (row) => {
      const items = Array.isArray(row?.sourceSnapshot?.items)
        ? row.sourceSnapshot.items
        : [];
      setSnapshotItems(items);
      setHasSnapshot(true);
    });
  }, [baseKey, fieldName, nodeKey, recipe.items.length]);

  const progress = useMemo(() => {
    if (!recipe.items.length) return EMPTY_PROGRESS;
    if (!hasSnapshot) {
      if (String(task?.status || '').trim() === 'complete') {
        return {
          totalItems: recipe.items.length,
          completedItems: recipe.items.length,
          isComplete: true,
        };
      }
      return EMPTY_PROGRESS;
    }
    return computeWorkflowTaskChecklistProgress(recipe.items, snapshotItems);
  }, [hasSnapshot, recipe.items, snapshotItems, task?.status]);

  return {
    progress,
    percentLabel: formatWorkflowTaskChecklistPercent(progress),
  };
}
